import React, { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { AMBIENT_COLOR, KEY_LIGHT_COLOR, FILL_LIGHT_COLOR } from './twinColors';

/**
 * SiteScene - Three.js Canvas container for the Digital Twin 3D viewport.
 *
 * Dark background with bright, colorful equipment using Vortx brand palette.
 *
 * @param {{ simTime: string|null, children: React.ReactNode }} props
 */
export default function SiteScene({ simTime, children }) {
    const isNight = useMemo(() => {
        if (!simTime) return false;
        const hour = new Date(simTime).getHours();
        return hour < 6 || hour > 19;
    }, [simTime]);

    return (
        <Canvas
            camera={{ position: [45, 35, 50], fov: 50 }}
            gl={{ antialias: true }}
            style={{ width: '100%', height: '100%', background: '#0a0f0e' }}
        >
            {/* Warm ambient — enough to see structure, brighter at night */}
            <ambientLight
                intensity={isNight ? 0.6 : 0.4}
                color={AMBIENT_COLOR}
            />

            {/* Key light from above-right */}
            <directionalLight
                position={[30, 60, 40]}
                intensity={0.5}
                color={KEY_LIGHT_COLOR}
                castShadow={false}
            />

            {/* Fill light from opposite side */}
            <directionalLight
                position={[-20, 30, -30]}
                intensity={0.25}
                color={FILL_LIGHT_COLOR}
            />

            {/* Orbit controls with damping and angle limits */}
            <OrbitControls
                enableDamping
                dampingFactor={0.05}
                minDistance={15}
                maxDistance={150}
                maxPolarAngle={Math.PI / 2.2}
            />

            {children}
        </Canvas>
    );
}
